import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';

import PatternIcon from '~/assets/icons/design-qr/pattern.svg?react';
import { ColorInputPicker } from '~/components/atoms/ColorInputPicker';
import DesignStepBlockHeaderLayout from '~/components/QRCodeDesign/DesignStepBlockHeaderLayout';
import Accordion from '~/components/ui/Accordion/Accordion';
import AsyncSwitch from '~/components/ui/AsyncSwitch';
import Select from '~/components/ui/Select';
import { Text } from '~/components/ui/text';
import { DEFAULT_QR_CODE_STYLES, DESKTOP_MIN_WIDTH, GRADIENT_TYPES } from '~/constants/common';
import { ShapeQrList } from '~/constants/shape';
import { ChangeGradientParams, QrCodeStyle } from '~/types/qrCode';

import { StyledGradientForm, StyledInfo, StyledShape, StyledShapeContainer, StyledShapesContainer } from './styled';

import styles from './styles.module.scss';

const DEFAULT_START_COLOR = '#000000';

type QRCodePatternProps = {
  defaultSelectedShape?: string;
  onSelectShape: (newShape: string) => void;
  isAdvanced?: boolean;
  onChangeColor?: (newColor: string) => void;
  onChangeGradient?: (params: ChangeGradientParams) => void;
  onChangeIsGradient?: (event: ChangeEvent<HTMLInputElement>) => void;
  qrCodeStyles?: QrCodeStyle;
  defaultExpanded?: boolean;
};

export default function QRCodePattern({
  defaultSelectedShape,
  onSelectShape,
  isAdvanced = false,
  onChangeColor,
  onChangeGradient,
  onChangeIsGradient,
  qrCodeStyles,
  defaultExpanded = false,
}: QRCodePatternProps) {
  const { t } = useTranslation();

  const [selectedShape, setSelectedShape] = useState(defaultSelectedShape || DEFAULT_QR_CODE_STYLES.shape.style);

  const handleSelectShape = (newShape: string) => {
    setSelectedShape(newShape);
    onSelectShape(newShape);
  };

  return (
    <Accordion
      variant="medium"
      defaultExpanded={defaultExpanded}
      summary={
        <DesignStepBlockHeaderLayout
          Icon={PatternIcon}
          title={t('builder:designStep.pattern.title')}
          description={t('builder:designStep.pattern.description')}
        />
      }
      sx={{ maxWidth: '100%' }}
      details={
        <>
          <StyledShapesContainer>
            {ShapeQrList.map(({ Icon, id }) => {
              const isSelected = selectedShape === id;

              return (
                <StyledShape key={id} $isSelected={isSelected} onClick={() => handleSelectShape(id)}>
                  <Icon width={36} height={36} className={isSelected ? styles.selectedIcon : undefined} />
                </StyledShape>
              );
            })}
          </StyledShapesContainer>
          {isAdvanced && (
            <AdvancedStyles
              onChangeColor={onChangeColor}
              onChangeGradient={onChangeGradient}
              onChangeIsGradient={onChangeIsGradient}
              qrCodeStyles={qrCodeStyles}
            />
          )}
        </>
      }
    />
  );
}

type AdvancedStylesProps = {
  onChangeColor: (newColor: string) => void;
  onChangeGradient: (params: ChangeGradientParams) => void;
  onChangeIsGradient: (event: ChangeEvent<HTMLInputElement>) => void;
  qrCodeStyles?: QrCodeStyle;
};

function AdvancedStyles({ onChangeColor, onChangeGradient, onChangeIsGradient, qrCodeStyles }: AdvancedStylesProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const [isFrameGradient, setIsFrameGradient] = useState(typeof qrCodeStyles?.shape?.color === 'object');

  const handleChangeIsGradient = (e: ChangeEvent<HTMLInputElement>) => {
    setIsFrameGradient(e.target.checked);
    onChangeIsGradient(e);
  };

  return (
    <>
      <StyledInfo>
        ℹ️
        <Text variant="inputSmall">{t('thirdStep:qrText')}</Text>
      </StyledInfo>
      <StyledShapeContainer>
        <Text variant="subheader">{t('builder:designStep.pattern.patternColor')}</Text>
        <AsyncSwitch
          label={t('thirdStep:useGradients')}
          checked={isFrameGradient}
          onChange={handleChangeIsGradient}
          sx={{ width: 'fit-content' }}
        />
        {isFrameGradient ? (
          <StyledGradientForm>
            <Select
              items={GRADIENT_TYPES}
              label={t('thirdStep:gradientType')}
              defaultValue={qrCodeStyles?.shape?.color?.type || GRADIENT_TYPES[0]?.value}
              onChange={(e) => onChangeGradient({ field: 'type', value: e.target.value })}
              fullWidth
              sx={{ flex: 1 }}
            />
            <ColorInputPicker
              label={t('thirdStep:color')}
              onChange={(value) => onChangeGradient({ field: 'start_color', value })}
              sx={{ flex: 1 }}
              value={qrCodeStyles?.shape?.color?.startColor ?? '#000000'}
              fullWidth
            />

            <ColorInputPicker
              label={t('thirdStep:color')}
              onChange={(value) => onChangeGradient({ field: 'end_color', value })}
              sx={{ flex: 1 }}
              value={qrCodeStyles?.shape?.color?.endColor ?? '#ffffff'}
              fullWidth
            />
          </StyledGradientForm>
        ) : (
          <ColorInputPicker
            onChange={onChangeColor}
            value={
              (typeof qrCodeStyles?.shape?.color === 'string'
                ? qrCodeStyles?.shape?.color
                : qrCodeStyles?.shape?.color?.color) ?? DEFAULT_START_COLOR
            }
            label={t('thirdStep:color')}
            fullWidth={!isDesktop}
          />
        )}
      </StyledShapeContainer>
    </>
  );
}
